import { useMemo } from "react";
import type { ReactNode } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { dateUtil } from "@sdeapps/react-core";
import ToastMessages from "constants/ToastMessages";
import type { Mandat } from "models";
import { enqueueSnackbar } from "notistack";
import { mandatsService } from "services";
import { useSnackbarErrorHandler } from "utils/errorHandling";
import { nbsp } from "constants/InputRules";

interface DeleteMandatDialogProps {
  open: boolean;
  handleClose: () => void;
  mandatSdea: Mandat;
  refetchElu?: () => void;
}

function getFormattedString(s: string | undefined): ReactNode {
  return s != null ? (
    <>
      {s}
      <br />
    </>
  ) : null;
}

function DeleteMandatDialog({
  open,
  handleClose,
  mandatSdea,
  refetchElu,
}: Readonly<DeleteMandatDialogProps>): ReactNode {
  const mandatDescription: ReactNode = useMemo(
    () => (
      <>
        <strong>{mandatSdea.libelleFonction}</strong>
        <br />
        {getFormattedString(mandatSdea.libelleTerritoire)}
        {getFormattedString(mandatSdea.libellePerimetre)}
        {getFormattedString(mandatSdea.libelleCollectivite)}
        {mandatSdea.dateDebutMandat != null &&
          ` à partir du ${dateUtil.format(mandatSdea.dateDebutMandat, "dd/MM/yyyy")}`}
        {mandatSdea.dateFinMandat != null &&
          ` jusqu'au ${dateUtil.format(mandatSdea.dateFinMandat, "dd/MM/yyyy")}`}
      </>
    ),
    [mandatSdea]
  );

  const { catchErrors, isLoading: isSending } = useSnackbarErrorHandler();

  function clotureMandat(): void {
    void catchErrors(async (): Promise<void> => {
      await mandatsService.deleteMandat(mandatSdea.id);

      enqueueSnackbar({
        variant: "success",
        message: ToastMessages.DELETED_MANDAT,
      });

      refetchElu?.();
    });
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Confirmation requise</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Êtes-vous sûr(e) de vouloir supprimer le mandat suivant{nbsp}?
          <br />
          <br />
          {mandatDescription}
          <br />
          <br />
          Cette opération est différente d'une clôture de mandat : elle est{" "}
          <strong>irréversible</strong>, et aucune trace de l'existence de ce mandat ne sera
          sauvegardée.
          <br />
          <br />
          <em>Pour clôturer un mandat, il sufit de renseigner sa date de fin.</em>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error" disabled={isSending}>
          Annuler
        </Button>
        <Button
          onClick={clotureMandat}
          variant="contained"
          loadingPosition="start"
          loading={isSending}>
          Oui, supprimer le mandat
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteMandatDialog;
